'use client';

import { c as _c } from "react/compiler-runtime";
import { getFormattedDateAndTime } from '@svenskaspelab/poolbetting/core/lib/client';
import type { Outcome } from '@svenskaspelab/poolbetting/schema/coupon';
import type { DrawNumber } from '@svenskaspelab/poolbetting/schema/draw';
import { type DateTime, type Definition } from '@svenskaspelab/poolbetting/schema/frameworks';
import type { CouponSettings } from '@svenskaspelab/poolbetting/schema/sessionstore';
import type { EventNumber, EventTypeStatistic, SportType } from '@svenskaspelab/poolbetting/schema/sport';
import { cn } from '@svenskaspelab/poolbetting/ui/utils';
import type { ReactNode } from 'react';
import { GameAnalysisContainer } from './GameAnalysisContainer';
import { GameAnalysisOutcomeContainer } from './GameAnalysisOutcomeContainer';
import { TipsinfoValue } from './TipsinfoValue';
type TipsinfoValuesProps = {
  eventStatistics: EventTypeStatistic;
  settings: CouponSettings;
  matchStart: DateTime | undefined;
  product: Definition['moduleEngine'];
  drawNumber: DrawNumber;
  eventNumber: EventNumber;
  sportType: SportType;
  className?: string;
};

/**
 * Tipsinfo values component.
 *
 * @param root0 props
 * @param root0.eventStatistics event statistics to get tipsinfo values for
 * @param root0.settings coupon settings to get tipsinfo values for
 * @param root0.matchStart time when match starts, used for accessibility purposes
 * @param root0.product product to get game analysis for
 * @param root0.drawNumber draw number to get game analysis for
 * @param root0.eventNumber event number to get game analysis for
 * @param root0.sportType sport type to get tipsinfo values for
 * @param root0.className optional class name for the component
 * @returns tipsinfo values presentational component
 */
export function TipsinfoValues(t0) {
  const $ = _c(52);
  const {
    eventStatistics,
    settings,
    matchStart,
    product,
    drawNumber,
    eventNumber,
    sportType,
    className
  } = t0;
  const {
    tipsinfo,
    gameAnalysis,
    outcomeLabel
  } = settings;
  let t1;
  if ($[0] !== outcomeLabel || $[1] !== tipsinfo) {
    t1 = tipsinfo.includes("SvenskaFolket") && outcomeLabel !== "LocalDistribution";
    $[0] = outcomeLabel;
    $[1] = tipsinfo;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const hasLocalDistribution = t1;
  let t2;
  if ($[3] !== tipsinfo) {
    t2 = tipsinfo.includes("Odds");
    $[3] = tipsinfo;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const hasOdds = t2;
  let t3;
  if ($[5] !== tipsinfo) {
    t3 = tipsinfo.includes("FavoriteOdds");
    $[5] = tipsinfo;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const hasFavoriteOdds = t3;
  let t4;
  if ($[7] !== tipsinfo) {
    t4 = tipsinfo.includes("StartOdds");
    $[7] = tipsinfo;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const hasStartOdds = t4;
  let t5;
  if ($[9] !== gameAnalysis) {
    t5 = gameAnalysis.includes("Tip");
    $[9] = gameAnalysis;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const hasGameAnalysisTip = t5;
  let t6;
  if ($[11] !== tipsinfo) {
    t6 = tipsinfo.includes("Matchstart");
    $[11] = tipsinfo;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const hasMatchStartTime = t6;
  const hasTipsInfo = tipsinfo.length > 0 || gameAnalysis.length > 0;
  const trendResetScopeBase = `${drawNumber}-${eventStatistics.id}`;
  if (!hasTipsInfo) {
    return;
  }
  const t7 = `text-layer-fg-muted mt-2 w-full text-sm lg:text-base ${className}`;
  let t8;
  if ($[13] !== t7) {
    t8 = cn(t7);
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <thead className="sr-only"><tr><th>Tipsinfo</th><th scope="col">1</th><th scope="col">X</th><th scope="col">2</th></tr></thead>;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== eventStatistics.distribution || $[17] !== hasLocalDistribution || $[18] !== trendResetScopeBase) {
    t10 = hasLocalDistribution && <TipsinfoValue metrics={eventStatistics.distribution} title="Svenska folket" hasTrend={true} trendResetScope={`${trendResetScopeBase}-distribution`} />;
    $[16] = eventStatistics.distribution;
    $[17] = hasLocalDistribution;
    $[18] = trendResetScopeBase;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== eventStatistics.odds || $[21] !== hasOdds || $[22] !== trendResetScopeBase) {
    t11 = hasOdds && <TipsinfoValue metrics={eventStatistics.odds} title="Aktuellt odds" hasTrend={true} trendResetScope={`${trendResetScopeBase}-odds`} />;
    $[20] = eventStatistics.odds;
    $[21] = hasOdds;
    $[22] = trendResetScopeBase;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== eventStatistics.favouriteOdds || $[25] !== hasFavoriteOdds) {
    t12 = hasFavoriteOdds && <TipsinfoValue metrics={eventStatistics.favouriteOdds} title="Favoritskap" />;
    $[24] = eventStatistics.favouriteOdds;
    $[25] = hasFavoriteOdds;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== eventStatistics.startOdds || $[28] !== hasStartOdds) {
    t13 = hasStartOdds && <TipsinfoValue metrics={eventStatistics.startOdds} title="Startodds" />;
    $[27] = eventStatistics.startOdds;
    $[28] = hasStartOdds;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== drawNumber || $[31] !== eventNumber || $[32] !== eventStatistics.eventType || $[33] !== hasGameAnalysisTip || $[34] !== product || $[35] !== sportType) {
    t14 = hasGameAnalysisTip && <GameAnalysisContainer product={product} drawNumber={drawNumber} eventNumber={eventNumber} renderItem={t15 => {
      const {
        eventAnalyses
      } = t15;
      return <GameAnalysisOutcomeContainer eventAnalyses={eventAnalyses} sportType={sportType} eventType={eventStatistics.eventType} eventNumber={eventNumber} />;
    }} />;
    $[30] = drawNumber;
    $[31] = eventNumber;
    $[32] = eventStatistics.eventType;
    $[33] = hasGameAnalysisTip;
    $[34] = product;
    $[35] = sportType;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== t10 || $[38] !== t11 || $[39] !== t12 || $[40] !== t13 || $[41] !== t14) {
    t15 = <tbody className="grid w-full">{t10}{t11}{t12}{t13}{t14}</tbody>;
    $[37] = t10;
    $[38] = t11;
    $[39] = t12;
    $[40] = t13;
    $[41] = t14;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== t15 || $[44] !== t8) {
    t16 = <table className={t8}>{t9}{t15}</table>;
    $[43] = t15;
    $[44] = t8;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== hasMatchStartTime || $[47] !== matchStart) {
    t17 = hasMatchStartTime && matchStart && <span className="sr-only">{`Matchstart: ${getFormattedDateAndTime(matchStart, {
        shouldCapitalizeFirstLetter: true,
        shouldUseNameOfDay: true,
        dateFormatOptions: "dayAsDateMonthAsText",
        formatOptions: "dateAndTime"
      })}`}</span>;
    $[46] = hasMatchStartTime;
    $[47] = matchStart;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== t16 || $[50] !== t17) {
    t18 = <>{t16}{t17}</>;
    $[49] = t16;
    $[50] = t17;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  return t18;
}